import { useMemo } from 'react';
import { useGetRoleManagementUsersMeRole, useGetRoleManagementUsersMePermissions } from '../api/role-management-api/role-management-api';
import { useGetAuthUser } from '../api/auth-api/auth-api';
import { AccessLevel, PermissionKey, PermissionScope, RolePermissionDto } from '../api/model';

// Mirrors the backend Operation enum
export type Operation = 'READ' | 'CREATE' | 'UPDATE' | 'DELETE' | 'RELEASE';

function allows(level: AccessLevel | undefined, op: Operation): boolean {
  if (!level || level === AccessLevel.NONE) return false;
  switch (op) {
    case 'READ':
      return true;
    case 'CREATE':
      return level === AccessLevel.CREATE || level === AccessLevel.EDIT || level === AccessLevel.FULL;
    case 'UPDATE':
      return level === AccessLevel.EDIT || level === AccessLevel.FULL;
    case 'DELETE':
      return level === AccessLevel.FULL;
    case 'RELEASE':
      return level === AccessLevel.RELEASE || level === AccessLevel.FULL;
    default:
      return false;
  }
}

export interface UseCurrentUserPermissionsResult {
  roleKey: string | null;
  roleDisplayName: string | null;
  permissions: RolePermissionDto[];
  isSystemAdmin: boolean;
  isLoadingRole: boolean;
  isLoadingPermissions: boolean;
  isLoading: boolean;
  // operation-based gate
  can: (key: PermissionKey, op: Operation) => boolean;
  // resolved access level / scope for a key
  levelFor: (key: PermissionKey) => AccessLevel | undefined;
  scopeFor: (key: PermissionKey) => PermissionScope | undefined;
  canCreate: (key: PermissionKey) => boolean;
  canEdit: (key: PermissionKey) => boolean;
  canRead: (key: PermissionKey) => boolean;
  canDelete: (key: PermissionKey) => boolean;
  canRelease: (key: PermissionKey) => boolean;
}

export function useCurrentUserPermissions(): UseCurrentUserPermissionsResult {
  const { data: roleData, isLoading: isLoadingRole } = useGetRoleManagementUsersMeRole({
    query: { staleTime: 5 * 60 * 1000, retry: false },
  });

  const { data: permsData, isLoading: isLoadingPermissions } = useGetRoleManagementUsersMePermissions({
    query: { staleTime: 5 * 60 * 1000, retry: false },
  });

  const { data: authUser } = useGetAuthUser({
    query: { staleTime: 5 * 60 * 1000, retry: false },
  });

  // system admins have no matrix role NO FILTER FOR SYSTEM&* SUPER ADMIBN
  const isSystemAdmin = authUser?.role === 'SUPER_ADMIN';

  const permissions = useMemo(() => permsData ?? [], [permsData]);

  const permissionMap = useMemo(() => {
    const map = new Map<PermissionKey, RolePermissionDto>();
    permissions.forEach(p => {
      if (p.permissionKey) map.set(p.permissionKey, p);
    });
    return map;
  }, [permissions]);

  const levelFor = (key: PermissionKey) =>
    isSystemAdmin ? AccessLevel.FULL : permissionMap.get(key)?.accessLevel;
  const scopeFor = (key: PermissionKey) =>
    isSystemAdmin ? PermissionScope.UNLIMITED : permissionMap.get(key)?.scope;

  const can = (key: PermissionKey, op: Operation): boolean =>
    isSystemAdmin || allows(permissionMap.get(key)?.accessLevel, op);

  return {
    roleKey: roleData?.roleKey ?? null,
    roleDisplayName: roleData?.roleDisplayName ?? null,
    permissions,
    isSystemAdmin,
    isLoadingRole,
    isLoadingPermissions,
    isLoading: isLoadingRole || isLoadingPermissions,
    can,
    levelFor,
    scopeFor,
    canCreate: (key) => can(key, 'CREATE'),
    canEdit: (key) => can(key, 'UPDATE'),
    canRead: (key) => can(key, 'READ'),
    canDelete: (key) => can(key, 'DELETE'),
    canRelease: (key) => can(key, 'RELEASE'),
  };
}
